import { useSession } from '@airship-platform/airship-auth';
import { useToast } from '@airship-platform/airship-patterns';
import {
  EmailValidationSchema,
  PasswordValidationSchema,
  PhoneValidationSchema,
} from '@airship-platform/common/validation';
import {
  CheckInput,
  FlexBox,
  Loader,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import { EyeIcon, EyeIconOff } from '@airship-platform/jam-web/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import useOrganization from '../../../data-access/hooks/useOrganizations';
import { formatPhone } from '../../../data-access/utils/phone.utils';
import ROUTE_PATHS from '../../../routes/paths';

const schema = z.object({
  organizationName: z.string().min(1, 'Company Name is required'),
  firstName: z.string().min(1, 'First Name is required'),
  lastName: z.string().min(1, 'Last Name is required'),
  email: EmailValidationSchema.min(1, 'Email is required'),
  phone: PhoneValidationSchema,
  password: PasswordValidationSchema,
  agreed: z
    .boolean()
    .refine((val) => val === true, 'You must agree to the terms'),
});

type FormInputs = z.infer<typeof schema>;

const SignUpForm = () => {
  const { showToast } = useToast();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      organizationName: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      agreed: false,
    },
    resolver: zodResolver(schema),
  });

  const navigate = useNavigate();
  const { createOrganization, isLoading } = useOrganization();
  const { signIn, buildSignInUrl } = useSession();

  const onSubmit = async (data: FormInputs) => {
    const organization = await createOrganization(data);
    if (!organization.success) {
      return;
    }

    setIsLoggingIn(true);
    try {
      await signIn(data.email, data.password);

      navigate('/onboarding');
    } catch (error) {
      showToast(
        error instanceof Error ? error.toString() : 'Error signing in',
        'error'
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      autoComplete="off"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: (theme) => theme.palette.common.white,
        padding: '4.75rem',
        position: 'relative',
        boxShadow:
          '0px 2px 8px -2px rgba(21, 21, 21, 0.08), 0px 12px 16px -4px rgba(21, 21, 21, 0.08)',
        justifyContent: 'center',
      }}
    >
      <FlexBox flexDirection={'column'} width={'25rem'} gap={'1rem'}>
        <Typography variant="h2" gutterBottom>
          Join Airship
        </Typography>
        <Controller
          name="organizationName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Company Name"
              error={!!errors.organizationName}
              helperText={errors.organizationName?.message}
              fullWidth
              slotProps={{
                htmlInput: { 'data-testid': 'organizationName' },
              }}
            />
          )}
        />
        <FlexBox gap={2}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="First Name"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                slotProps={{
                  htmlInput: { 'data-testid': 'firstName' },
                }}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Last Name"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                slotProps={{
                  htmlInput: { 'data-testid': 'lastName' },
                }}
              />
            )}
          />
        </FlexBox>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Email Address"
              error={!!errors.email}
              helperText={errors.email?.message}
              fullWidth
              slotProps={{
                htmlInput: { 'data-testid': 'email' },
              }}
              autoComplete="username"
            />
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Phone Number"
              type="tel"
              error={!!errors.phone}
              helperText={errors.phone?.message}
              fullWidth
              slotProps={{
                htmlInput: {
                  'data-testid': 'phone',
                  onInput: formatPhone,
                },
              }}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Password"
              type={isPasswordVisible ? 'text' : 'password'}
              error={!!errors.password}
              helperText={errors.password?.message}
              fullWidth
              autoComplete="current-password"
              slotProps={{
                input: {
                  endAdornment: (
                    <Typography
                      component="span"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      {isPasswordVisible ? <EyeIconOff /> : <EyeIcon />}
                    </Typography>
                  ),
                },
                htmlInput: { 'data-testid': 'password' },
              }}
            />
          )}
        />
        <Controller
          name="agreed"
          control={control}
          render={({ field }) => (
            <CheckInput
              {...field}
              label={
                <Typography variant="subtitle2">
                  I agree to Airship's{' '}
                  <Link href={ROUTE_PATHS.TERMS_OF_SERVICE}>
                    Terms of Service
                  </Link>
                </Typography>
              }
              error={errors.agreed?.message}
            />
          )}
        />
        <LoadingButton
          type="submit"
          fullWidth
          disabled={isLoading}
          loading={isLoading}
        >
          {'Create account'}
        </LoadingButton>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2">
            Already have an account? {''}
            <Link
              href={buildSignInUrl(
                `${window.location.origin}/signup/onboarding`
              )}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </FlexBox>
      <Loader isLoading={isLoggingIn} />
    </Box>
  );
};

export default SignUpForm;
